import {
  Component,
  ChangeDetectionStrategy,
  ComponentRef,
  SimpleChange,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  tap,
  filter,
  Subscription,
  of,
  switchMap,
  finalize,
  shareReplay,
  map,
} from 'rxjs';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

import {
  DprDetailsSync,
  DprSummary,
  DprSummaryAttachment,
} from '@shared/models/dpr';
import { DprDetailsGridContainerBaseComponent } from '@dpr-details/models/component';
import { GridApi, GridSync } from '@grid/models';

import {
  DprCommentAttachmentsComponent,
  DprCommentComponent,
} from '@dpr-details/components';

import { DprDetailsService } from '@dpr-details/services';
import { DprSummaryGridSyncResult } from '@dpr-details/models/sync';

import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { SummaryTabs } from '@dpr-details/enums';
import { DprAttachmentService } from '@shared/services/dpr';
import { NavigationMenuItem } from '@shared/models';
import { ScreenSizeService } from '@shared/services';
import { Constants } from '@shared/utils';

@UntilDestroy()
@Component({
  selector: 'app-dpr-summary-page',
  templateUrl: './dpr-summary-page.component.html',
  styleUrls: ['./dpr-summary-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprSummaryPageComponent
  extends DprDetailsGridContainerBaseComponent<DprAttachmentsGridData>
  implements OnInit
{
  currentDprSummaryLabel: string = $localize`:@@dprDetails.dprSummaryPage.txtCurrentDprSummary:Current DPR Summary`;
  nextPlanningLabel: string = $localize`:@@dprDetails.dprSummaryPage.txtNext24hPlanning:Next 24h Planning`;
  externalPartyCommentLabel: string = $localize`:@@dprDetails.dprSummaryPage.txtExternalPartyComment:External Party Comment`;

  currentComment: string;
  currentAttachmentsChanges: GridSync<DprAttachmentsGridData>;
  currentAttachmentsGridData: DprAttachmentsGridData[];

  nextComment: string;
  nextAttachmentsChanges: GridSync<DprAttachmentsGridData>;
  nextAttachmentsGridData: DprAttachmentsGridData[];

  externalComment: string;

  currentGridApi!: GridApi<DprAttachmentsGridData>;
  nextGridApi!: GridApi<DprAttachmentsGridData>;
  shouldDisplayExternalComment: boolean;
  canEditExternalComment: boolean;
  externalCommentTabName: string;
  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = [
    {
      id: 1,
      label: this.currentDprSummaryLabel,
      link: SummaryTabs.CurrentDprSummary,
    },
    {
      id: 2,
      label: this.nextPlanningLabel,
      link: SummaryTabs.Next24hPlanning,
    },
    {
      id: 3,
      label: this.externalPartyCommentLabel,
      link: SummaryTabs.ExternalPartyComment,
    },
  ];

  SummaryTabs = SummaryTabs;

  private activatedComponent$ = new BehaviorSubject<ComponentRef<any>>(null);
  private dataReady$ = new BehaviorSubject<boolean>(false);
  private gridOfflineChangesReady$ = new BehaviorSubject<boolean>(false);
  private currentSummaryChangesSub!: Subscription;
  private currentSummaryGridRegistrationSub!: Subscription;
  private nextPlanningChangesSub!: Subscription;
  private nextPlanningGridRegistrationSub!: Subscription;
  private externalCommentChangesSub!: Subscription;

  constructor(
    private router: Router,
    private dprDetailsService: DprDetailsService,
    private dprAttachmentService: DprAttachmentService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    protected activatedRoute: ActivatedRoute,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute);
  }

  private get subTab(): SummaryTabs {
    const parts = this.router.url.split('/');

    return parts[parts.length - 1] as SummaryTabs;
  }

  private get dprSummary(): DprSummary {
    return this.dprDetails.dprSummary;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.dprDetailsOfflineChanges$
      .pipe(
        tap((dprDetailsOfflineChanges) => {
          // timeout is needed to execute this after the grid changes are cleared following a successful save
          setTimeout(() => {
            this.buildGridsChanges(dprDetailsOfflineChanges);
            this.gridOfflineChangesReady$.next(true);
          });
        })
      )
      .subscribe();

    combineLatest([
      this.gridOfflineChangesReady$,
      this.selectedSubproject$,
      this.dprDetails$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([gridOfflineChangesReady]) => gridOfflineChangesReady),
        tap(([_, selectedSubproject, dprDetails]) => {
          this.refreshFlags();

          this.setupData();

          this.shouldDisplayExternalComment =
            selectedSubproject.data?.subprojectConfiguration.showExternalPartyComment;
          this.canEditExternalComment =
            selectedSubproject.canEditExternalPartyComment &&
            !selectedSubproject.isLocked &&
            !dprDetails.lockedDate;
          this.externalCommentTabName =
            selectedSubproject.data.subprojectConfiguration?.customNames
              ?.externalPartyCommentName || this.externalPartyCommentLabel;

          this.navigationSelectItems[2].label = this.externalCommentTabName;

          this.dataReady$.next(true);
        })
      )
      .subscribe();

    combineLatest([this.activatedComponent$, this.dataReady$])
      .pipe(
        untilDestroyed(this),
        filter(
          ([activatedComponent, dataReady]) => !!activatedComponent && dataReady
        ),
        tap(([event]) => {
          this.onActivatedComponentChanged(event);
        })
      )
      .subscribe();

    this.preselectedItem = this.subTab;
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges();

    const saveChanges$ = validateChanges$.pipe(
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(isAutoSyncFromAdminChange);
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onComponentActivated(component: ComponentRef<any>): void {
    this.activatedComponent$.next(component);
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  //#region Refresh data

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected refreshData(): void {
    this.currentAttachmentsChanges = null;
    this.nextAttachmentsChanges = null;

    this.refreshFlags();
  }

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprSummaryAttachment> {
    return dprDetailsOfflineChanges?.dprSummary?.syncDprSummaryAttachments;
  }

  //#endregion

  //#region Private methods

  private refreshFlags(): void {
    this.currentGridApi?.reset();
    this.nextGridApi?.reset();

    if (this.activatedComponent instanceof DprCommentAttachmentsComponent) {
      this.activatedComponent?.gridHasChanges$.next(false);
    }

    this.isDirty$.next(false);
  }

  private setupData(): void {
    this.currentComment = this.dprSummary.currentComment;
    this.currentAttachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprSummary.dprSummaryAttachments.filter((x) => x.isCurrentDPR)
    );

    this.nextComment = this.dprSummary.nextComment;
    this.nextAttachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprSummary.dprSummaryAttachments.filter((x) => !x.isCurrentDPR)
    );

    this.externalComment = this.dprSummary.externalComment;
  }

  private validateChanges(): Observable<boolean> {
    if (
      !this.dprAttachmentService.validateChanges(
        this.currentAttachmentsChanges
      ) ||
      !this.dprAttachmentService.validateChanges(this.nextAttachmentsChanges)
    ) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprSummaryGridSyncResult> {
    const syncAttachments = this.unifyAttachments();

    const syncData = this.dprDetailsSyncModelBuilderService.forDprSummary(
      this.selectedSubproject.id,
      this.dprDetails.id,
      this.dprSummary.id,
      syncAttachments,
      this.currentComment,
      this.nextComment,
      this.externalComment
    );

    const saveChanges$ = this.dprDetailsService
      .syncDprSummary(
        syncData,
        !this.canEdit && this.canEditExternalComment,
        isAutoSyncFromAdminChange
      )
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.summaryTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprSummaryGridSyncResult): void {
    this.refreshData();

    if (result.isExternalCommentResult) {
      this.offlineFacade.setDprSummaryExternalComment(
        result.dprSummary,
        result.firstWarning,
        result.warningStatusTab,
        result.dprDetailId
      );
    } else {
      this.offlineFacade.setDprSummary(
        result.dprSummary,
        result.firstWarning,
        result.warningStatusTab,
        result.dprDetailId
      );
    }
  }

  private onActivatedComponentChanged(component: ComponentRef<any>): void {
    if (component !== this.activatedComponent) {
      this.storeComponentChanges(this.activatedComponent);
    }

    this.setupRouteComponent(component);

    this.activatedComponent = component;
  }

  private storeComponentChanges(component: ComponentRef<any>): void {
    if (!component) {
      return;
    }

    if (component instanceof DprCommentAttachmentsComponent) {
      const summaryComponent =
        component as unknown as DprCommentAttachmentsComponent;

      if (summaryComponent.tab === SummaryTabs.CurrentDprSummary) {
        this.storeCurrentSummaryChanges(component);
      }

      if (summaryComponent.tab === SummaryTabs.Next24hPlanning) {
        this.storeNextPlanningChanges(component);
      }
    }

    if (component instanceof DprCommentComponent) {
      const commentComponent = component as unknown as DprCommentComponent;
      this.storeExternalCommentChanges(commentComponent);
    }
  }

  private storeCurrentSummaryChanges(
    component: DprCommentAttachmentsComponent
  ): void {
    this.currentComment =
      component.commentComponent.commentFormControl?.value?.trim();
    this.currentAttachmentsGridData = component.gridData.map((x) => ({
      ...x,
      isCurrentDPR: true,
    }));
    this.currentAttachmentsChanges = this.currentGridApi?.getChanges();
  }

  private storeNextPlanningChanges(
    component: DprCommentAttachmentsComponent
  ): void {
    this.nextComment =
      component.commentComponent.commentFormControl?.value?.trim();
    this.nextAttachmentsGridData = component.gridData.map((x) => ({
      ...x,
      isCurrentDPR: false,
    }));
    this.nextAttachmentsChanges = this.nextGridApi?.getChanges();
  }

  private storeExternalCommentChanges(component: DprCommentComponent): void {
    this.externalComment = component.commentFormControl?.value?.trim();
  }

  private setupRouteComponent(component: ComponentRef<any>): void {
    if (!component) {
      return;
    }

    if (this.subTab === SummaryTabs.CurrentDprSummary) {
      this.setupCurrentSummary(
        component as unknown as DprCommentAttachmentsComponent
      );
    }

    if (this.subTab === SummaryTabs.Next24hPlanning) {
      this.setupNextPlanning(
        component as unknown as DprCommentAttachmentsComponent
      );
    }

    if (
      this.subTab === SummaryTabs.ExternalPartyComment &&
      this.shouldDisplayExternalComment
    ) {
      this.setupExternalComment(component as unknown as DprCommentComponent);
    }

    // if external party comments disabled, navigate to first subtab
    if (
      this.subTab === SummaryTabs.ExternalPartyComment &&
      !this.shouldDisplayExternalComment
    ) {
      this.router.navigate([SummaryTabs.CurrentDprSummary], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
      });
    }

    this.setupCanEditBinding();
    this.setupIsAppOnlineBinding();
  }

  private setupCurrentSummary(component: DprCommentAttachmentsComponent): void {
    component.gridData = this.currentAttachmentsGridData;
    component.predefinedChanges = this.currentAttachmentsChanges;
    component.canEdit = this.canEdit;
    component.selectedSubproject = this.selectedSubproject;
    component.comment = this.currentComment;
    component.clientId = this.selectedClientId;
    component.isAppOnline = this.isAppOnline;
    component.tab = SummaryTabs.CurrentDprSummary;
    component.commentComponent.commentFormControl.setValue(this.currentComment);
    component.maxLength = Constants.FieldLimits.maxSummaryCommentLength;

    this.currentSummaryChangesSub?.unsubscribe();
    this.currentSummaryChangesSub = component.dataChanged
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isDirty$.next(true);
      });

    this.currentSummaryGridRegistrationSub?.unsubscribe();
    this.currentSummaryGridRegistrationSub = component.gridRegistered
      .pipe(untilDestroyed(this))
      .subscribe((gridApi) => {
        this.currentGridApi = gridApi;
      });
  }

  private setupNextPlanning(component: DprCommentAttachmentsComponent): void {
    component.gridData = this.nextAttachmentsGridData;
    component.predefinedChanges = this.nextAttachmentsChanges;
    component.canEdit = this.canEdit;
    component.selectedSubproject = this.selectedSubproject;
    component.comment = this.nextComment;
    component.clientId = this.selectedClientId;
    component.isAppOnline = this.isAppOnline;
    component.tab = SummaryTabs.Next24hPlanning;
    component.commentComponent.commentFormControl.setValue(this.nextComment);
    component.maxLength = Constants.FieldLimits.maxSummaryCommentLength;

    this.nextPlanningChangesSub?.unsubscribe();
    this.nextPlanningChangesSub = component.dataChanged
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isDirty$.next(true);
      });

    this.nextPlanningGridRegistrationSub?.unsubscribe();
    this.nextPlanningGridRegistrationSub = component.gridRegistered
      .pipe(untilDestroyed(this))
      .subscribe((gridApi) => {
        this.nextGridApi = gridApi;
      });
  }

  private setupExternalComment(component: DprCommentComponent): void {
    component.comment = this.externalComment;
    component.canEdit = this.canEditExternalComment;
    component.maxLength = Constants.FieldLimits.maxSummaryCommentLength;
    component.commentFormControl.setValue(this.externalComment);

    const changes: SimpleChanges = {
      comment: new SimpleChange(null, this.dprSummary.externalComment, true),
      canEdit: new SimpleChange(null, this.canEditExternalComment, true),
    };

    component.ngOnChanges(changes);

    this.externalCommentChangesSub?.unsubscribe();
    this.externalCommentChangesSub = component.commentChanged
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isDirty$.next(true);
      });
  }

  private buildGridsChanges(dprDetailsOfflineChanges: DprDetailsSync): void {
    const offlineChanges = this.getOfflineAttachmentsChanges(
      dprDetailsOfflineChanges
    );

    this.currentAttachmentsChanges = {
      addedList: this.dprAttachmentService.mapToGridData(
        (offlineChanges?.addedList ?? []).filter((x) => x.isCurrentDPR)
      ),
      updatedList: this.dprAttachmentService.mapToGridData(
        (offlineChanges?.updatedList ?? []).filter((x) => x.isCurrentDPR)
      ),
      deletedIds: offlineChanges?.deletedIds ?? [],
    };

    this.nextAttachmentsChanges = {
      addedList: this.dprAttachmentService.mapToGridData(
        (offlineChanges?.addedList ?? []).filter((x) => !x.isCurrentDPR)
      ),
      updatedList: this.dprAttachmentService.mapToGridData(
        (offlineChanges?.updatedList ?? []).filter((x) => !x.isCurrentDPR) ?? []
      ),
      deletedIds: offlineChanges?.deletedIds ?? [],
    };
  }

  /**
   * @description Combines the next and current attachments into one single sync object as server processes them as one.
   */
  private unifyAttachments(): GridSync<DprSummaryAttachment> {
    return {
      addedList: [
        ...this.dprAttachmentService
          .mapToAttachments(this.currentAttachmentsChanges?.addedList ?? [])
          ?.map((x) => ({
            ...x,
            isCurrentDPR: true,
            dprDetailId: this.dprDetails.id,
          })),
        ...this.dprAttachmentService
          .mapToAttachments(this.nextAttachmentsChanges?.addedList ?? [])
          ?.map((x) => ({
            ...x,
            isCurrentDPR: false,
            dprDetailId: this.dprDetails.id,
          })),
      ],
      updatedList: [
        ...this.dprAttachmentService
          .mapToAttachments(this.currentAttachmentsChanges?.updatedList ?? [])
          ?.map((x) => ({
            ...x,
            isCurrentDPR: true,
            dprDetailId: this.dprDetails.id,
          })),
        ...this.dprAttachmentService
          .mapToAttachments(this.nextAttachmentsChanges?.updatedList ?? [])
          ?.map((x) => ({
            ...x,
            isCurrentDPR: false,
            dprDetailId: this.dprDetails.id,
          })),
      ],
      deletedIds: Array.from(
        new Set([
          ...(this.currentAttachmentsChanges?.deletedIds ?? []),
          ...(this.nextAttachmentsChanges?.deletedIds ?? []),
        ])
      ),
    };
  }

  //#endregion
}
